import { BorrowerContractualizationResponse } from 'api/borrower';
import { ReferentialDto } from 'api/referential';
import GenericTabs from 'components/navigation/Tabs/Tabs';
import { NsKey } from 'enums/NsKey';
import { BorrowerPricingDetails } from 'features/subscription-process/contractualization-process/components/borrowers/BorrowerPricingDetails';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { FC } from 'react';
import { BorrowerLoanFormFieldNames, BorrowerLoanFormValues } from './borrowerLoanForm';
import { BorrowerLoanForm } from './BorrowerLoanFormComponent';
import { useBorrowerLoanPanel } from './useBorrowerLoanPanel';

interface ReferentialLabels {
  loanTypes: ReferentialDto[];
  interestRateTypes: ReferentialDto[];
  defermentTypes: ReferentialDto[];
}

interface BorrowerLoanPanelProps {
  borrowerId: string;
  onSuccess: (response: BorrowerContractualizationResponse) => void;
  referentials: ReferentialLabels;
  isRenego?: boolean;
  disabledFields?: Array<Exclude<keyof BorrowerLoanFormValues, 'id'>>;
  showInlineSaveButton?: boolean;
}

export const BorrowerLoanPanel: FC<BorrowerLoanPanelProps> = ({
  borrowerId,
  onSuccess,
  referentials,
  isRenego = true,
  disabledFields = [BorrowerLoanFormFieldNames.LoanType],
  showInlineSaveButton = false
}) => {
  const { t } = useDynamicTranslation([
    NsKey.Common,
    NsKey.Pricing,
    NsKey.Borrower
  ]);

  const {
    loans,
    isLoading,
    activeTab,
    handleTabChange,
    isSubmitting,
    formikRef,
    handleSubmit,
    handleValidationChange,
    handleDirtyChange,
    mapLoanToFormValues
  } = useBorrowerLoanPanel({ borrowerId, onSuccess, referentials });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!loans || loans.length === 0) {
    return (
      <div className="text-center text-gray-500 p-8">{t('COMMON.NO_DATA')}</div>
    );
  }

  return (
    <GenericTabs defaultTab={activeTab} onChange={handleTabChange}>
      {loans.map((loan, index) => (
        <GenericTabs.Item
          key={loan.id}
          label={`${t('COMMON.LABELS.LOAN')} ${index + 1}`}
          value={index}
        />
      ))}
      {loans.map((loan, index) => (
        <GenericTabs.Content key={loan.id} value={index}>
          <div className="p-4 space-y-6">
            <BorrowerLoanForm
              formikRef={formikRef}
              initialValues={mapLoanToFormValues(loan)}
              onSubmit={handleSubmit}
              onValidationChange={handleValidationChange}
              onDirtyChange={handleDirtyChange}
              isRenego={isRenego}
              disabledFields={disabledFields}
              isSubmitting={isSubmitting}
              showInlineSaveButton={showInlineSaveButton}
            />

            {/* Pricing details */}
            {loan.pricing && (
              <BorrowerPricingDetails
                pricing={loan.pricing}
                guarantees={loan.guarantees}
                showDiscount={(loan.pricing.discountPrice ?? 0) > 0}
              />
            )}
          </div>
        </GenericTabs.Content>
      ))}
    </GenericTabs>
  );
};
